import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  EmptyState,
  ErrorState,
  FormActions,
  PageHeader,
  Skeleton,
} from '../../../ui';
import StepperVistoria from '../../../components/vistoria/StepperVistoria';
import DocumentoVistoria from '../../../components/vistoria/DocumentoVistoria';

import { RootState } from '../../../store/store';
import { setPdfImageUrl } from '../../../store/vistoriaSlice';
import {
  atualizarVistoriaAccessories,
  buscarVistoriaPorId,
  gerarLaudoPdfVistoria,
  VistoriaPorIdResponse,
} from '../../../api/vistoria/vistoriaApi';
import {
  enviarFotosVistoriaPorPlaca,
  enviarVistoriaPorId,
  ErroEnvioSga,
} from '../../../api/sga-vistoria/sga-vistoriaApi';
import { agruparChecklist } from '../../../utils/vistoriaAcessorios';
import { normalizarUrlImagem, ordenarAvariasPrimeiro } from '../../../utils/vistoriaImagens';
import { rotuloEnvioSga, situacaoDasEtapasDaVistoria } from '../../../utils/vistoriaFluxo';
import { totalFotosEsperado } from '../../../utils/vistoriaLegendas';
import { HOME } from '../../home/routes';
import {
  VISTORIA,
  VISTORIA_ASSINATURA,
  VISTORIA_CAPTURA,
  VISTORIA_CHECKLIST_ACESSORIOS,
} from '../routes';

/**
 * `/vistoria/laudo` — **etapa 4 e fim do fluxo de vistoria** (reescrita na at-64,
 * frente D/D2). É aqui que a vistoria é finalizada (gerar o PDF) e onde o envio
 * ao SGA pode ser antecipado.
 *
 * ## O que mudou de comportamento
 *
 *   1. 🐛 **Finalizar não apaga mais o checklist salvo.** A tela enviava
 *      `accessories` **do Redux** no `PATCH` de finalização, e o backend
 *      substitui o campo inteiro. Retomando uma vistoria pela `/carteira`,
 *      `hidratarFluxoVistoria` reseta aquele slice — então "Finalizar" gravava
 *      vazio por cima das 23 respostas do banco, e a trava `podeFinalizar` não
 *      pegava (ela lê o banco, que estava completo). Hoje o `PATCH` leva **só**
 *      `pdf_image_url`, e o documento é exibido **do banco**.
 *   2. 🐛 **As fotos vêm do banco, não do Redux.** Eram `vistoria.images` (blob
 *      URLs que morrem no reload, gravadas só na PRIMEIRA tentativa de envio) com
 *      um segundo GET de fallback. Quando o laudo abre, toda foto já está no GCS
 *      — o upload só responde depois de persistir (at-24, *"estado real, lido do
 *      banco"*).
 *   3. **O card "Editar" de 4 botões virou as bolinhas do `StepperVistoria`**
 *      (pedido de Victor: bolinhas dinâmicas em todas as telas). A **lista textual
 *      de bloqueios com o atalho "resolver" FICA** — a bolinha vermelha diz *que*
 *      há pendência, não *quantas fotos faltam*, e `title` não existe no celular
 *      (at-24). Mas ela deriva do **mesmo** `situacaoDasEtapasDaVistoria` que
 *      pinta as bolinhas: com fontes separadas, uma hora as duas discordariam.
 *   4. **Uma leitura só do banco**, passada ao `StepperVistoria` pela prop
 *      `vistoria` — a tela relê depois de gerar o PDF, e duas leituras
 *      independentes ficariam momentaneamente em desacordo.
 *   5. **Falha e vazio ganharam voz**: leitura do banco falhando mostra
 *      `ErrorState` com "Tentar novamente" (era silenciosa) e sem `vistoriaId` há
 *      `EmptyState` com o caminho ao painel.
 *   6. **Os chips de envio ao SGA são ÂMBAR**, não vermelhos: "aguardando envio"
 *      não é erro — o cron envia a cada 2h (spec 02 §2.3; textos e variantes vêm
 *      de `rotuloEnvioSga`).
 *
 * ⚠️ **O que NÃO mudou:** a sequência gerar PDF → `PATCH` → releitura (a corrida
 * do `pdf_image_url` continua real: quem grava é o PATCH, não a geração), a lógica
 * de envio ao SGA (ordem laudo → fotos, **409 = sucesso**, fotos exigem o veículo
 * no SGA, e os textos que dizem que o cron cobre) e o compartilhamento por
 * WhatsApp com o link público do PDF.
 */
const VistoriaLaudoPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const voltar = useVoltar(VISTORIA());

  /** Aberta pelas bolinhas em modo de correção? A marca é preservada. */
  const origemEdicao = (location.state as any)?.origemEdicao === true;

  const vistoria = useSelector((state: RootState) => state.vistoria);
  const associado = useSelector((state: RootState) => state.associado);

  const vistoriaId = vistoria.vistoriaId;
  const placa = vistoria.vehicle?.placa || '';

  /**
   * A vistoria como está no BANCO — fonte única desta tela.
   *
   * Alimenta ao mesmo tempo: o documento (fotos, checklist, observação,
   * assinatura, PDF), a lista de bloqueios, as bolinhas (via prop) e o card de
   * encerramento. Antes eram três leituras com três propósitos.
   */
  const [doBanco, setDoBanco] = React.useState<VistoriaPorIdResponse | null>(null);
  const [carregando, setCarregando] = React.useState(Boolean(vistoriaId));
  const [leituraFalhou, setLeituraFalhou] = React.useState(false);

  const [gerando, setGerando] = React.useState(false);
  const [enviandoSga, setEnviandoSga] = React.useState(false);
  /** Espelha o `envioSga` do banco e é atualizado pelo botão, sem reler. */
  const [envioSga, setEnvioSga] = React.useState({
    laudoEnviado: false,
    fotosEnviadas: false,
    veiculoNoSga: false,
  });

  /**
   * Leitura do estado da vistoria no banco.
   *
   * ⚠️ Chamada em dois momentos, e é por isso que é uma função: ao montar a tela e
   * logo DEPOIS de persistir o laudo. O `pdf_image_url` não é gravado pela geração
   * do PDF (que só devolve a URL do GCS) e sim pelo PATCH seguinte — sem a segunda
   * chamada, a tela não mostraria o encerramento justo no momento em que a
   * vistoria foi finalizada.
   */
  const carregarDoBanco = React.useCallback(
    async (id: string, aindaValido: () => boolean = () => true) => {
      setCarregando(true);
      setLeituraFalhou(false);
      try {
        const resposta = await buscarVistoriaPorId({ vistoriaId: id });
        if (!aindaValido()) return;

        setDoBanco(resposta);
        if (resposta?.envioSga) setEnvioSga(resposta.envioSga);
      } catch {
        if (!aindaValido()) return;
        /*
         * ⚠️ Falha aqui deixa `doBanco` nulo, e com isso `podeFinalizar` é
         * `false`: as travas continuam **conservadoras** (nunca liberam algo por
         * falta de dado). O que mudou é que agora a tela DIZ que não conseguiu
         * ler, em vez de ficar em branco e travada.
         */
        setDoBanco(null);
        setLeituraFalhou(true);
      } finally {
        if (aindaValido()) setCarregando(false);
      }
    },
    [],
  );

  React.useEffect(() => {
    if (!vistoriaId) {
      setCarregando(false);
      return undefined;
    }

    let ativo = true;
    void carregarDoBanco(vistoriaId, () => ativo);

    return () => {
      ativo = false;
    };
  }, [vistoriaId, carregarDoBanco]);

  /* ── O que falta para finalizar, derivado da MESMA fonte das bolinhas ── */

  const totalEsperado = totalFotosEsperado(vistoria.vehicle?.tipoVeiculo);
  const fotosNoBanco = Array.isArray(doBanco?.fotos) ? doBanco!.fotos!.length : 0;
  const fotosFaltando = Math.max(0, totalEsperado - fotosNoBanco);

  /**
   * Tudo que impede finalizar, em texto para o consultor ler.
   *
   * 🔴 Deriva de `situacaoDasEtapasDaVistoria(…, 4).pendentes` — a mesma função
   * que pinta as bolinhas de vermelho. Só desabilitar o botão não comunica nada, e
   * o `title` não existe no celular, que é onde a vistoria é feita (at-24).
   */
  const pendentes = doBanco
    ? situacaoDasEtapasDaVistoria(
        { vistoria: doBanco, tipoVeiculo: vistoria.vehicle?.tipoVeiculo },
        4,
      ).pendentes
    : [];

  const TEXTO_PENDENCIA: Record<number, string> = {
    1: `${fotosFaltando} de ${totalEsperado} foto(s) ainda não foram enviadas`,
    2: 'O checklist de acessórios está incompleto',
    3: 'Falta a assinatura do associado',
  };

  const bloqueios = pendentes
    .filter((etapa) => etapa in TEXTO_PENDENCIA)
    .map((etapa) => ({ etapa, texto: TEXTO_PENDENCIA[etapa] }));

  const concluida = Boolean(doBanco?.pdf_image_url);
  /** Sem leitura do banco não se libera nada — trava conservadora (at-24). */
  const podeFinalizar = Boolean(doBanco) && bloqueios.length === 0 && !concluida;

  /** O PDF: preferimos o do banco; o Redux cobre o instante após gerar. */
  const pdfUrl = doBanco?.pdf_image_url || vistoria.pdf_image_url || null;

  /* ── Navegação para corrigir uma etapa (as bolinhas fazem o mesmo) ── */

  const irParaEtapa = (etapa: number) => {
    /**
     * `origemEdicao` marca que a etapa foi aberta para CORRIGIR, não para avançar.
     * Cada tela consulta a marca e, ao salvar, volta para o laudo — sem isso,
     * editar uma foto arrastava o consultor por checklist e assinatura, e a tela
     * de assinatura captura uma NOVA assinatura, sobrescrevendo a que o associado
     * já havia dado.
     */
    const marca = { origemEdicao: true };

    if (etapa === 1) {
      navigate(VISTORIA_CAPTURA(), { state: marca });
      return;
    }
    if (etapa === 2) {
      navigate(VISTORIA_CHECKLIST_ACESSORIOS(), { state: marca });
      return;
    }
    navigate(VISTORIA_ASSINATURA(), {
      state: { associadoId: associado?.associadoId, carroId: vistoria.vehicle?.id, ...marca },
    });
  };

  /* ── Finalizar: gerar o PDF ── */

  const finalizar = async () => {
    if (!vistoriaId || !podeFinalizar) return;

    setGerando(true);
    try {
      /*
       * Geração 100% no backend, sem enviar HTML/imagens/base64: ele baixa as
       * fotos pelas URLs do GCS e monta o registro fotográfico, com as avarias em
       * seção destacada (at-24 — antes o PDF não tinha foto alguma).
       */
      const resposta = await gerarLaudoPdfVistoria({ vistoriaId });
      const urlGerada: string | undefined = (resposta as any)?.pdfUrl;

      if (!urlGerada) {
        toast.error('Backend não retornou o link do PDF.');
        return;
      }

      // Já no estado para liberar o WhatsApp, mesmo se a persistência falhar.
      dispatch(setPdfImageUrl(urlGerada));

      try {
        /*
         * 🔴 **Só `pdf_image_url`.** Mandar `accessories` daqui era o que apagava
         * o checklist do banco — ver o cabeçalho desta tela.
         */
        await atualizarVistoriaAccessories({ vistoriaId, pdfImageUrl: urlGerada });
        toast.success('PDF gerado com sucesso.');

        // Só agora o banco sabe que a vistoria está concluída — é esta releitura
        // que faz o card de encerramento aparecer na hora.
        await carregarDoBanco(vistoriaId);
      } catch (erro: any) {
        const mensagem =
          erro?.response?.data?.message || erro?.response?.data?.error || erro?.message;
        toast.warning(
          mensagem
            ? `PDF gerado, mas não foi possível salvar o link no backend: ${mensagem}`
            : 'PDF gerado, mas não foi possível salvar o link no backend.',
        );
      }
    } catch (erro: any) {
      const mensagem =
        erro?.response?.data?.message || erro?.response?.data?.error || erro?.message;
      toast.error(
        mensagem ? `Falha ao gerar PDF: ${mensagem}` : 'Falha ao gerar PDF no backend.',
      );
    } finally {
      setGerando(false);
    }
  };

  /* ── Envio ao SGA (at-31): ANTECIPA o cron, nunca é pré-requisito ── */

  const podeEnviarAgora =
    !envioSga.laudoEnviado || (!envioSga.fotosEnviadas && envioSga.veiculoNoSga);

  const enviarAoSga = async () => {
    if (!vistoriaId || enviandoSga) return;

    setEnviandoSga(true);

    // 409 = "já enviado". Para o consultor isso é sucesso (o cron pode ter chegado
    // primeiro), não erro — o chip fica verde do mesmo jeito.
    const jaEnviado = (erro: unknown) => (erro as ErroEnvioSga)?.status === 409;

    const detalhar = (erro: unknown) => {
      const mensagem = (erro as Error)?.message?.trim();
      return mensagem ? ` (${mensagem})` : '';
    };

    try {
      if (!envioSga.laudoEnviado) {
        try {
          await enviarVistoriaPorId(vistoriaId);
        } catch (erro) {
          if (!jaEnviado(erro)) throw erro;
        }
        setEnvioSga((atual) => ({ ...atual, laudoEnviado: true }));
      }
    } catch (erro) {
      toast.error(
        `Não foi possível enviar o laudo agora${detalhar(erro)}. A vistoria está salva — o sistema tentará de novo automaticamente a cada 2 horas.`,
      );
      setEnviandoSga(false);
      return;
    }

    // Fotos exigem o veículo já cadastrado no SGA (`codigo_veiculo`). Quando ele
    // ainda não está lá, isso não é falha: o cron envia assim que o veículo subir.
    if (!envioSga.fotosEnviadas) {
      if (!envioSga.veiculoNoSga) {
        toast.info(
          'Laudo enviado ao SGA. As fotos serão enviadas automaticamente assim que o veículo estiver cadastrado no SGA.',
        );
        setEnviandoSga(false);
        return;
      }

      try {
        try {
          await enviarFotosVistoriaPorPlaca(placa);
        } catch (erro) {
          if (!jaEnviado(erro)) throw erro;
        }
        setEnvioSga((atual) => ({ ...atual, fotosEnviadas: true }));
        toast.success('Vistoria enviada ao SGA (laudo e fotos).');
      } catch (erro) {
        toast.warning(
          `Laudo enviado, mas as fotos não foram${detalhar(erro)}. O sistema tentará enviá-las automaticamente a cada 2 horas.`,
        );
      }
      setEnviandoSga(false);
      return;
    }

    toast.success('Vistoria enviada ao SGA.');
    setEnviandoSga(false);
  };

  /* ── O documento, montado a partir do banco ── */

  const checklist = React.useMemo(
    () => agruparChecklist(doBanco?.accessories?.items ?? []),
    [doBanco],
  );

  const fotos = React.useMemo(
    () =>
      ordenarAvariasPrimeiro(doBanco?.fotos ?? []).map((foto) => ({
        url: normalizarUrlImagem(foto.url),
        legenda: foto.caption ?? undefined,
        avaria: foto.damaged === true,
      })),
    [doBanco],
  );

  /**
   * A assinatura vem do banco, com o Redux como **fallback**: a tela de assinatura
   * grava nos dois, e isso cobre o instante entre o `POST signature` e a releitura.
   */
  const assinaturaUrl =
    normalizarUrlImagem(doBanco?.signature_image_url) || vistoria.signatureBase64 || null;

  const chipLaudo = rotuloEnvioSga(envioSga.laudoEnviado, 'Laudo');
  const chipFotos = envioSga.fotosEnviadas
    ? rotuloEnvioSga(true, 'Fotos')
    : envioSga.veiculoNoSga
      ? rotuloEnvioSga(false, 'Fotos')
      : ({ texto: 'Fotos aguardando o veículo no SGA', variante: 'pendente' } as const);

  return (
    <ShellSaac
      cabecalho={<PageHeader sobretitulo="Vistoria" titulo="Laudo de vistoria" aoVoltar={voltar} />}
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              {/*
                🔴 A fonte é a leitura DESTA tela (P8 do plano): ela relê o banco
                depois de gerar o PDF e depois de voltar de uma correção, e duas
                leituras independentes deixariam as bolinhas e a lista de
                bloqueios em desacordo por um instante.
              */}
              <StepperVistoria
                etapa={4}
                vistoria={doBanco}
                leituraFalhou={leituraFalhou}
              />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              {!vistoriaId ? (
                <EmptyState
                  titulo="Nenhuma vistoria em andamento"
                  descricao="O laudo é a última etapa de uma vistoria. Vá ao painel, escolha o veículo e inicie (ou continue) a vistoria por lá."
                  icone="alerta"
                  acao={
                    <Botao variante="primario" onClick={() => navigate(VISTORIA())}>
                      Ir para o painel
                    </Botao>
                  }
                />
              ) : carregando ? (
                <Skeleton linhas={6} altura="tw-h-14" />
              ) : leituraFalhou ? (
                <ErrorState
                  mensagem="Não foi possível carregar esta vistoria."
                  detalhe="O laudo mostra o que está gravado no servidor, então nada pode ser exibido nem finalizado enquanto a leitura não voltar."
                  aoTentarNovamente={() => void carregarDoBanco(vistoriaId)}
                  ocupado={carregando}
                />
              ) : (
                <>
                  {/*
                    ═══ Topo: um dos dois, nunca os dois ═══
                    Enquanto não está concluída, o que falta (ou a dica de que dá
                    para corrigir); depois de concluída, o encerramento. É a regra
                    da at-24/at-31: a tela sempre diz em que ponto o processo está.
                  */}
                  {!concluida && bloqueios.length > 0 && (
                    <div className="tw-mb-6 tw-rounded-xl tw-border tw-border-danger/40 tw-border-l-4 tw-border-l-danger tw-bg-danger-bg tw-p-3">
                      <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-danger">
                        Ainda não é possível finalizar esta vistoria:
                      </p>
                      <ul className="tw-mb-0 tw-mt-2 tw-flex tw-flex-col tw-gap-1.5 tw-pl-4 tw-text-sm tw-text-texto-corpo">
                        {bloqueios.map((bloqueio) => (
                          <li key={bloqueio.etapa}>
                            {bloqueio.texto}{' '}
                            <Botao
                              variante="fantasma"
                              tamanho="sm"
                              onClick={() => irParaEtapa(bloqueio.etapa)}
                            >
                              resolver
                            </Botao>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!concluida && bloqueios.length === 0 && (
                    <p className="tw-mb-6 tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-superficie tw-px-3 tw-py-2.5 tw-text-xs tw-text-texto-suave">
                      As alterações valem enquanto a vistoria não for finalizada — use as
                      etapas acima para corrigir fotos, checklist ou assinatura.
                    </p>
                  )}

                  {concluida && (
                    <div className="tw-mb-6 tw-rounded-xl tw-border tw-border-success tw-bg-success-bg tw-p-3">
                      <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-texto-titulo">
                        Vistoria concluída
                      </p>

                      <div className="tw-mt-2 tw-flex tw-flex-wrap tw-gap-2">
                        <Chip variante={chipLaudo.variante}>{chipLaudo.texto}</Chip>
                        <Chip variante={chipFotos.variante}>{chipFotos.texto}</Chip>
                      </div>

                      {envioSga.laudoEnviado && envioSga.fotosEnviadas ? (
                        <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
                          Nada mais pendente — laudo e fotos já estão no SGA.
                        </p>
                      ) : !podeEnviarAgora ? (
                        /* Só faltam as fotos e o veículo ainda não está no SGA: não
                           há o que o botão possa fazer agora (as fotos exigem
                           `codigo_veiculo`). Mostrar um botão que não age seria pior
                           do que explicar a espera. */
                        <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-texto-suave">
                          O laudo já está no SGA. As fotos serão enviadas automaticamente
                          assim que o veículo estiver cadastrado lá.
                        </p>
                      ) : (
                        <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2">
                          <Botao
                            variante="primario"
                            carregando={enviandoSga}
                            disabled={enviandoSga}
                            onClick={() => void enviarAoSga()}
                          >
                            {enviandoSga ? 'Enviando ao SGA...' : 'Enviar ao SGA agora'}
                          </Botao>
                          {/* O botão é atalho, não obrigação: quem não clicar não
                              perde nada. Dizer isso evita que uma falha do SGA
                              pareça perda do trabalho. */}
                          <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
                            O envio é automático a cada 2 horas. Use o botão para enviar na
                            hora.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  <DocumentoVistoria
                    associado={{
                      nome: associado?.nomeCompleto,
                      cpf: associado?.cpfCnpj || vistoria.cpf,
                    }}
                    veiculo={{
                      placa: vistoria.vehicle?.placa,
                      modelo: vistoria.vehicle?.modelo,
                      anoModelo: vistoria.vehicle?.anoModelo,
                      anoFabricacao: vistoria.vehicle?.anoFabricacao,
                    }}
                    checklist={checklist}
                    observacao={doBanco?.accessories?.observacao}
                    fotos={fotos}
                    assinaturaUrl={assinaturaUrl}
                    pdfUrl={pdfUrl}
                    /* A seção "PDF" aparece mesmo sem link: é onde o consultor vê
                       que o documento ainda não foi gerado. */
                    secoes={{ pdf: true }}
                  />

                  {/* Repetido junto ao botão principal: quem rolou o laudo até o
                      fim não viu (nem lembra) o aviso do topo, e é aqui que vai
                      tentar clicar. */}
                  {!concluida && bloqueios.length > 0 && (
                    <div className="tw-mt-6 tw-rounded-xl tw-border tw-border-danger/40 tw-bg-danger-bg tw-p-3">
                      <p className="tw-m-0 tw-text-sm tw-font-bold tw-text-danger">
                        O botão está bloqueado porque falta:
                      </p>
                      <ul className="tw-mb-0 tw-mt-1.5 tw-pl-4 tw-text-sm tw-text-texto-corpo">
                        {bloqueios.map((bloqueio) => (
                          <li key={bloqueio.etapa}>{bloqueio.texto}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* `larguraTotal` + `sm:w-auto`: coluna no mobile, lado a lado no
                      desktop — o padrão do projeto para mais de um botão na barra
                      (precedente em `/cotacao-produtos`, at-62). */}
                  <FormActions>
                    {!concluida && (
                      <Botao
                        variante="primario"
                        larguraTotal
                        className="sm:tw-w-auto"
                        carregando={gerando}
                        disabled={gerando || !podeFinalizar}
                        onClick={() => void finalizar()}
                      >
                        {/* É esta ação que conclui a vistoria: gerar o PDF grava o
                            `pdf_image_url`, e é isso que a tira de "em andamento"
                            no painel. */}
                        {gerando ? 'Finalizando...' : 'Finalizar vistoria (gerar PDF)'}
                      </Botao>
                    )}

                    {/*
                      🔴 **Terminado o fluxo, a barra oferece a SAÍDA** (pedido de
                      Victor, 10/09/2026).

                      Esta é a tela terminal da vistoria: depois de gerar o PDF não
                      há próxima etapa, e sem isto o consultor precisava do "Voltar"
                      do cabeçalho, do botão do navegador ou do menu para sair. É a
                      mesma lacuna que a at-62 corrigiu em `/cotacao-produtos`, que
                      também não tem tela terminal depois de si.

                      ⚠️ **Primário, seguindo o `/concluido`** (a tela terminal da
                      adesão, que usa `primario` + ícone `inicio` para ir à Home):
                      aqui não há outra ação de avanço com que competir —
                      compartilhar o laudo é opcional. Na cotação o "Início" é
                      `fantasma` porque lá existe um "Nova cotação" primário.

                      ⚠️ **Só quando CONCLUÍDA.** Antes disso a ação da tela é
                      finalizar, e oferecer a saída ao lado dela convidaria a
                      abandonar o trabalho — a mesma razão que a at-62 registrou
                      para não mostrar a saída antes de gerar a cotação.

                      📌 **Não limpa o estado da vistoria ao sair, de propósito.**
                      Diferente do `/concluido` da adesão (que despacha
                      `iniciarNovaAdesao` no mount), aqui não é preciso: a porta de
                      entrada do fluxo — `hidratarFluxoVistoria`, usada pelo painel
                      e pela carteira — já faz `resetInspection()` antes de navegar.
                      Limpar aqui só atrapalharia quem volta ao laudo para
                      compartilhar o PDF.
                    */}
                    {concluida && (
                      <Botao
                        variante="primario"
                        iconeEsquerda="inicio"
                        larguraTotal
                        className="sm:tw-w-auto"
                        onClick={() => navigate(HOME())}
                      >
                        Início
                      </Botao>
                    )}

                    <Botao
                      variante="secundario"
                      larguraTotal
                      className="sm:tw-w-auto"
                      disabled={!pdfUrl}
                      onClick={() =>
                        pdfUrl &&
                        // Regra: compartilhar apenas o link.
                        window.open(`https://wa.me/?text=${encodeURIComponent(pdfUrl)}`, '_blank')
                      }
                    >
                      Compartilhar no WhatsApp
                    </Botao>
                  </FormActions>

                  {origemEdicao && (
                    <p className="tw-mt-3 tw-text-center tw-text-xs tw-text-texto-desabilitado">
                      Você chegou aqui corrigindo uma etapa desta vistoria.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </ShellSaac>
  );
};

export default VistoriaLaudoPage;
